/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import { ChartBar, ChevronRight } from "lucide-react";
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import Pagination from "src/common/pagination";
import AppContext from "src/components/AppContext";
import { DataPoint, fetchWrapper } from "src/helpers";
import {
  formatDate,
  formatNumber,
  getDisplayLabel,
  getLocalStorageUser,
} from "src/helpers/api/sharedFunction";
import { getSortIcon } from "src/helpers/api/sortconfig";
import { getFormattedDate } from "src/helpers/dateFormatter";
import InsiderChartModal from "./chartModal";
import InsiderFilter from "./InsiderFilter";
import { colorMapping } from "./InsiderStateCard";
import InsiderDetailSkeleton from "src/components/skeleton/InsiderDetailSkeleton";
import { useSocket } from "src/components/SocketContext";
import { InsiderTrade, SubInsiderTrading } from "pages/insider-trading";

interface InsiderTableDetail {
  insiderTradingData: InsiderTrade[];
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  currentPage: number;
  totalItems: number;
  rowsPerPage: number;
  getSubInsiderTradingData: (
    symbol: string,
    personCategory: string,
    transactionType: string,
    acquisitionMode: string,
    broadcastDate: string,
  ) => Promise<void>;
  subInsiderTradingData: Record<string, SubInsiderTrading[]>;
  transactionType: never[];
  persontype: never[];
  marketCapType: never[];
  symbolType: never[];
  setSelectedPersonType: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedTransactionType: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectMarketCap: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectSymbol: React.Dispatch<React.SetStateAction<string[]>>;
  selectMarketCap: string[];
  selectedPersonType: string[];
  selectedTransactionType: string[];
  selectSymbol: string[];
  handleSort: (columnKey: string) => void;
  setRowsPerPage: Dispatch<SetStateAction<number>>;
  sortConfig: {
    key: string;
    direction: "desc" | "asc" | null;
  };
  isLoading?: boolean;
}

export default function InsiderDetailTable(props: InsiderTableDetail) {
  const {
    insiderTradingData,
    setCurrentPage,
    rowsPerPage,
    totalItems,
    currentPage,
    getSubInsiderTradingData,
    subInsiderTradingData,
    transactionType,
    persontype,
    marketCapType,
    setSelectedPersonType,
    setSelectedTransactionType,
    selectedPersonType,
    selectedTransactionType,
    selectMarketCap,
    setSelectMarketCap,
    selectSymbol,
    symbolType,
    setSelectSymbol,
    handleSort,
    setRowsPerPage,
    sortConfig,
    isLoading,
  } = props;
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [selectedSymbolName, setSelectedSymbolName] = useState<string>("");
  const [aquisitionFromDate, setAquisitionFromDate] = useState<string>("");
  const [aquisitionToDate, setAquisitionToDate] = useState<string>("");
  const [subListingtransactionType, setSubListingTransactionType] =
    useState<string>("");
  const user = getLocalStorageUser() || {};
  const token = user?.token;
  const [candledata, setCandleData] = useState<DataPoint[]>([]);
  const { emit, isConnected } = useSocket();
  const [candleLoading, setCandleLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const context = useContext(AppContext);
  const { darkMode } = context.state;

  const getCandleChartData = async (symbol: string) => {
    try {
      setCandleLoading(true);
      const response = await fetchWrapper.get(
        `/api/dashboard/ohlcdata?name=${encodeURIComponent(symbol)}`,
        undefined,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      setCandleData(response?.data);
    } finally {
      setCandleLoading(false);
    }
  };

  const toggleRow = async (trade: InsiderTrade) => {
    const key = `${trade.stock_symbol}-${trade.person_category}-${trade.transaction_type}-${trade.acquisition_mode}-${trade.broadcast_date}`;
    const isCurrentlyExpanded = expandedRows[key];
    if (!isCurrentlyExpanded) {
      if (!subInsiderTradingData[key]) {
        await getSubInsiderTradingData(
          trade.stock_symbol,
          trade.person_category,
          trade.transaction_type,
          trade.acquisition_mode,
          trade.broadcast_date,
        );
      }
    }
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSymbolName("");
  };

  const sortableColumns: any = {
    total_price: "Amount",
    holding_change: "Holding Change",
    broadcast_date: "Broadcast Date",
  };

  useEffect(() => {
    if (!selectedSymbolName || !isConnected) return;

    emit("subscribe_stock", { symbol: [selectedSymbolName] });

    return () => {
      emit("unsubscribe_stock", { symbol: [selectedSymbolName] });
    };
  }, [selectedSymbolName, isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <InsiderFilter
        persontype={persontype}
        marketCapType={marketCapType}
        symbolType={symbolType}
        setSelectSymbol={setSelectSymbol}
        selectSymbol={selectSymbol}
        transactionType={transactionType}
        setSelectedPersonType={setSelectedPersonType}
        setSelectedTransactionType={setSelectedTransactionType}
        selectedPersonType={selectedPersonType}
        selectedTransactionType={selectedTransactionType}
        selectMarketCap={selectMarketCap}
        setSelectMarketCap={setSelectMarketCap}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
      <div className="flex flex-col h-[calc(100vh-330px)] min-h-[330px] sm:h-[calc(100vh-350px)] md:h-[calc(100vh-536px)] lg:h-[calc(100vh-436px)]">
        <div className="bg-white dark:bg-[#121419] shadow-[5px_1px_5px_rgba(0,0,0,0.09)] overflow-hidden flex flex-col h-full">
          {/* Table container with fixed header */}
          <div className="flex flex-col flex-1 overflow-hidden">
            <table className="min-w-full w-full">
              <colgroup>
                <col className="w-[25%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[10%]" />
              </colgroup>
              <thead className="sticky top-0 z-20">
                <tr className="bg-[#f4f5f8] dark:bg-[#30324e]">
                  {[
                    "Stock & Person",
                    "Category",
                    "Transaction",
                    "Amount",
                    "Holding Change",
                    "Broadcast Date",
                  ].map((header, index) => {
                    const key = Object.keys(sortableColumns).find(
                      (k) => sortableColumns[k] === header,
                    );
                    return (
                      <th
                        key={header}
                        className={`text-left py-2 px-2 text-sm font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree ${
                          index === 0 ? "pl-6" : ""
                        } ${key ? "cursor-pointer group" : ""} 
                          after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[1px] after:bg-[#d0d4e4] dark:after:bg-[#394564]
                          before:content-[''] before:absolute before:left-0 before:top-0 before:w-full before:h-[1px] before:bg-[#d0d4e4] dark:before:bg-[#394564]
                        `}
                        onClick={() => key && handleSort(key)}
                      >
                        <div className="relative flex items-center">
                          {header}
                          {key && getSortIcon(sortConfig, key)}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
            </table>

            {/* Scrollable tbody wrapper */}
            <div className="flex-1 overflow-auto">
              <table className="min-w-full w-full">
                <colgroup>
                  <col className="w-[25%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[10%]" />
                </colgroup>
                <tbody>
                  {isLoading ? (
                    <InsiderDetailSkeleton />
                  ) : insiderTradingData.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400 text-sm font-medium">
                          No transactions have been found for the selected date
                          range.
                        </div>
                      </td>
                    </tr>
                  ) : (
                    insiderTradingData.map(
                      (
                        trade: InsiderTrade,
                        i: React.Key | null | undefined,
                      ) => {
                        const key = `${trade.stock_symbol}-${trade.person_category}-${trade.transaction_type}-${trade.acquisition_mode}-${trade.broadcast_date}`;
                        const isExpanded = expandedRows[key];

                        return (
                          <React.Fragment key={i}>
                            <tr
                              className="border-b border-[#d0d4e4] dark:border-[#394564] hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                              onClick={() => toggleRow(trade)}
                            >
                              <td className="py-0.5 px-2 font-figtree text-sm">
                                <div className="flex items-start">
                                  <ChevronRight
                                    className={`h-4 w-4 mr-2 mt-1 transform transition-transform ${
                                      expandedRows[key] ? "rotate-90" : ""
                                    }`}
                                  />

                                  <div>
                                    <div className="flex items-center">
                                      <div className="font-medium text-gray-900 dark:text-white">
                                        {trade.company_name}
                                      </div>
                                      <div className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                        {trade.stock_symbol}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="py-0.5 px-2 font-figtree text-sm">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium  max-w-max ${trade.person_category === "-" ? "" : "dark:text-blue-200 text-blue-800 dark:bg-blue-900  bg-blue-100"}`}
                                >
                                  {getDisplayLabel(trade.person_category)}
                                </span>
                              </td>
                              <td className="py-0.5 px-2 font-figtree text-sm">
                                <div className="flex items-center">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      colorMapping[trade.transaction_type]
                                        ?.bg || "bg-gray-100 dark:bg-gray-700"
                                    } ${colorMapping[trade.transaction_type]?.text || "text-gray-800 dark:text-gray-200"}`}
                                  >
                                    {getDisplayLabel(trade.transaction_type)}
                                  </span>
                                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                    {getDisplayLabel(trade.acquisition_mode)}
                                  </span>
                                </div>
                              </td>
                              <td className="py-0.5 px-2 font-figtree text-sm">
                                <div className="font-medium">
                                  {formatNumber(trade.total_price)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {trade.share_count.toLocaleString()} shares @
                                  ₹{trade.price}
                                </div>
                              </td>
                              <td className="py-0.5 px-2 font-figtree text-sm">
                                <div className="flex flex-col w-fit items-center">
                                  <div className="text-sm">
                                    {trade.prior_shareholding_percent}% →{" "}
                                    {trade.post_shareholding_percent}%
                                  </div>
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium mt-1 w-fit ${
                                      Number(trade.holding_change) >= 0
                                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    }`}
                                  >
                                    {trade.holding_change}
                                  </span>
                                </div>
                              </td>
                              <td className="py-0.5 px-2 font-figtree text-sm text-gray-500 dark:text-gray-400">
                                {trade.broadcast_date
                                  ? getFormattedDate(trade.broadcast_date)
                                  : "N/A"}
                              </td>
                            </tr>

                            {isExpanded &&
                              subInsiderTradingData[key]?.length > 0 && (
                                <>
                                  {/* Header row for sub-rows */}
                                  <tr className="bg-gray-50 dark:bg-gray-800/50">
                                    <td className="py-0.5 px-8 text-xs font-medium text-gray-500 dark:text-gray-400"></td>
                                    <td className="py-0.5 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Transaction Type
                                    </td>
                                    <td className="py-0.5 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Price
                                    </td>
                                    <td className="py-0.5 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Shares
                                    </td>
                                    <td className="py-0.5 px-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                                      Value
                                    </td>
                                    <td className="py-0.5 px-2"></td>
                                  </tr>

                                  {/* Sub-rows */}
                                  {subInsiderTradingData[key].map(
                                    (subTrade, subIndex) => (
                                      <tr
                                        key={`${key}-${subIndex}`}
                                        className="bg-gray-50 dark:bg-gray-800/50"
                                      >
                                        <td className="py-0.5 px-4 overflow-visible">
                                          <div className="flex items-center">
                                            <div className="w-4 flex-shrink-0"></div>
                                            <div className="min-w-0 flex flex-wrap item-center gap-2">
                                              <div className="relative">
                                                <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[180px] group">
                                                  <div className="truncate">
                                                    {getDisplayLabel(
                                                      subTrade.acquirer_disposer_name,
                                                    )}
                                                  </div>
                                                  {subTrade
                                                    .acquirer_disposer_name
                                                    ?.length > 25 && (
                                                    <div className="absolute z-50 left-1/2 transform -translate-x-1/2 -top-2 -translate-y-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-max max-w-[200px]">
                                                      <div className="bg-gray-200 dark:bg-gray-800 text-black dark:text-white text-xs px-3 py-0.5 rounded shadow-lg break-words whitespace-normal text-center">
                                                        {getDisplayLabel(
                                                          subTrade.acquirer_disposer_name,
                                                        )}
                                                      </div>
                                                      <div className="w-3 h-3 bg-gray-200 dark:bg-gray-800 absolute left-1/2 bottom-0 transform -translate-x-1/2 translate-y-1/2 rotate-45"></div>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="text-xs leading-[1.8]">
                                                {subTrade.acquisition_from_date &&
                                                  subTrade.acquisition_to_date &&
                                                  `
                                                  ${formatDate(
                                                    subTrade.acquisition_from_date,
                                                  )} -
                                                  ${formatDate(
                                                    subTrade.acquisition_to_date,
                                                  )}
                                                `}
                                              </div>
                                            </div>
                                          </div>
                                        </td>

                                        <td className="py-0.5 px-2">
                                          <div
                                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                              colorMapping[
                                                subTrade.transaction_type
                                              ]?.bg ||
                                              "bg-gray-100 dark:bg-gray-700"
                                            } ${colorMapping[subTrade.transaction_type]?.text || "text-gray-800 dark:text-gray-200"}`}
                                          >
                                            {getDisplayLabel(
                                              subTrade.transaction_type,
                                            )}
                                          </div>
                                        </td>

                                        <td className="py-0.5 px-2">
                                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {formatNumber(
                                              Number(subTrade.price),
                                            )}
                                          </div>
                                        </td>

                                        <td className="py-0.5 px-2">
                                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {subTrade.share_count.toLocaleString()}
                                          </div>
                                        </td>

                                        <td className="py-0.5 px-2">
                                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {formatNumber(subTrade.total_price)}
                                          </div>
                                        </td>
                                        <td className="py-0.5 px-2 text-gray-500 dark:text-gray-400 ">
                                          <button
                                            className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedSymbolName(
                                                trade.stock_symbol,
                                              );
                                              setAquisitionFromDate(
                                                subTrade.acquisition_from_date,
                                              );
                                              setSubListingTransactionType(
                                                subTrade.transaction_type,
                                              );
                                              setAquisitionToDate(
                                                subTrade.acquisition_to_date,
                                              );
                                              getCandleChartData(
                                                trade.stock_symbol,
                                              );
                                              setShowModal(true);
                                            }}
                                          >
                                            <ChartBar className="h-5 w-5 cursor-pointer" />
                                          </button>
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </>
                              )}
                          </React.Fragment>
                        );
                      },
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Fixed Pagination at bottom */}
          {insiderTradingData.length > 0 && (
            <div className="border-t border-[#d0d4e4] dark:border-[#394564]">
              <Pagination
                currentPage={currentPage}
                totalItems={totalItems}
                itemsPerPage={rowsPerPage}
                onPageChange={setCurrentPage}
                onRowsChange={setRowsPerPage}
              />
            </div>
          )}
        </div>
      </div>
      {showModal && (
        <InsiderChartModal
          isOpen={showModal}
          onClose={closeModal}
          stock={candledata}
          darkMode={darkMode}
          transactionType={subListingtransactionType}
          selectedSymbol={selectedSymbolName}
          aquisitionFromDate={aquisitionFromDate}
          aquisitionToDate={aquisitionToDate}
          candleLoading={candleLoading}
        />
      )}
    </>
  );
}
